import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { contactsApi } from '../api/contacts';

interface Contact { id: number; name: string; mobile?: string; ar_balance?: number; }

interface Props {
  value: string;
  onSelect: (contact: Contact) => void;
  placeholder?: string;
  showBalance?: boolean;
  initialLabel?: string;
  fontSize?: number;
}

const ContactPicker: React.FC<Props> = ({ value, onSelect, placeholder = 'Type to search customer...', showBalance = false, initialLabel, fontSize = 12 }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Contact[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(initialLabel || '');
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const [highlight, setHighlight] = useState(-1);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newMobile, setNewMobile] = useState('');
  const [addErr, setAddErr] = useState('');
  const [adding, setAdding] = useState(false);
  const [newRoles, setNewRoles] = useState<string[]>(['CUSTOMER']);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => { if (!value) setSelectedLabel(''); }, [value]);
  useEffect(() => { if (initialLabel) setSelectedLabel(initialLabel); }, [initialLabel]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      contactsApi.list({ search: query, limit: 50 })
        .then(r => { setResults(r.data.data || []); setHighlight(-1); })
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (inputRef.current && !inputRef.current.contains(t) && dropdownRef.current && !dropdownRef.current.contains(t)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (highlight >= 0 && itemRefs.current[highlight]) itemRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const openDropdown = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + window.scrollY, left: r.left + window.scrollX, width: r.width });
    }
    setOpen(true);
    setQuery('');
  };

  const handleSelect = (c: Contact) => {
    setSelectedLabel(c.name);
    setQuery('');
    setOpen(false);
    setHighlight(-1);
    onSelect(c);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') openDropdown(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (highlight >= 0 && results[highlight]) handleSelect(results[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); setHighlight(-1); }
  };

  const quickAdd = async () => {
    if (!newName.trim()) { setAddErr('Name is required'); return; }
    setAdding(true); setAddErr('');
    try {
      const res = await contactsApi.create({ name: newName.trim(), mobile: newMobile.trim() || null, roles: newRoles.length ? newRoles : ['CUSTOMER'] });
      const c = res.data.data;
      setSelectedLabel(c.name);
      setShowAdd(false); setNewName(''); setNewMobile('');
      onSelect(c);
    } catch (e: any) { setAddErr(e.response?.data?.message || 'Could not create contact'); }
    finally { setAdding(false); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display:'flex', gap:4 }}>
        <input
          ref={inputRef}
          className="form-control"
          style={{ fontSize, flex:1 }}
          placeholder={placeholder}
          value={open ? query : selectedLabel}
          onFocus={openDropdown}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button type="button" className="btn btn-secondary btn-sm" title="Add a new contact"
          style={{ padding:'0 10px', whiteSpace:'nowrap' }}
          onClick={() => { setShowAdd(true); setOpen(false); setNewName(query); setAddErr(''); }}>+ New</button>
      </div>
      {open && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999,
            background: '#ffffff', border: '1px solid var(--bdr)', borderRadius: 6,
            maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', marginTop: 2
          }}
        >
          {results.length === 0 ? (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--tx3)' }}>{query.trim() ? 'No matches' : 'Type to search...'}</div>
          ) : results.map((c, idx) => (
            <div
              key={c.id}
              ref={el => { itemRefs.current[idx] = el; }}
              onClick={() => handleSelect(c)}
              onMouseEnter={() => setHighlight(idx)}
              style={{
                padding: '8px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--bdr)',
                background: idx === highlight ? '#e8f0fe' : '#ffffff'
              }}
            >
              <div style={{ fontWeight: 600 }}>{c.name}</div>
              <div style={{ color: 'var(--tx3)', fontSize: 11 }}>
                {c.mobile || ''}{showBalance ? (c.mobile ? '  |  ' : '') + 'AR: ' + Number(c.ar_balance || 0).toFixed(2) : ''}
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}

      {showAdd && ReactDOM.createPortal(
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:10000}}>
          <div style={{background:'var(--sur)',borderRadius:8,width:360,padding:20,boxShadow:'0 8px 24px rgba(0,0,0,0.25)'}}>
            <div style={{fontSize:14,fontWeight:600,marginBottom:14}}>New contact</div>
            {addErr && <div style={{marginBottom:10,padding:8,background:'var(--sur2)',borderRadius:4,fontSize:12,color:'var(--red)'}}>{addErr}</div>}
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input className="form-control" value={newName} autoFocus
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') quickAdd(); }} />
            </div>
            <div className="form-group">
              <label className="form-label">Mobile</label>
              <input className="form-control" value={newMobile}
                onChange={e => setNewMobile(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') quickAdd(); }} />
            </div>
            <div style={{marginTop:14,display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button type="button" className="btn btn-secondary" onClick={() => { setShowAdd(false); setAddErr(''); }}>Cancel</button>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:'var(--tx3)',marginBottom:6}}>What are they?</div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {['CUSTOMER','SUPPLIER','EMPLOYEE','INVESTOR','OTHER'].map(r => (
                    <button key={r} type="button"
                      className={'filter-pill ' + (newRoles.includes(r) ? 'active' : '')}
                      onClick={() => setNewRoles(s => s.includes(r) ? s.filter(x => x !== r) : [...s, r])}>{r}</button>
                  ))}
                </div>
              </div>
              <button type="button" className="btn btn-primary" onClick={quickAdd} disabled={adding}>{adding ? 'Saving...' : 'Save and select'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ContactPicker;
